import { response } from './Responses.ts'
import { Plain, JSONValue } from './Assertions.ts'
import { Token, Scope } from '../js/Token.js'
import { Storage } from '../js/Storage.js'
import { HtmlContent } from './HtmlContent.ts'
import { YamlContent } from './YamlContent.ts'

// Daemon configuration looks like this.
export interface DaemonConfig {
  system: {
    protocol: 'http:' | 'https:'
    party: string
    issuer: string
    source: string
    sourcePrefix: string
    tab: string,
    privateJwk: JsonWebKey
  }
  content: {
    html: HtmlContent
    yaml?: YamlContent
  }
  user: Plain
}

// System requests are on the /daemon/... path.
export const DAEMON_PREFIX = 'daemon'

// Daemon lifecycle path components that come after DAEMON_PREFIX.
export const CONFIG_PATH = 'config'
export const EVENT_PATH = 'event'

// Event names.
const Ev = {
  Config: 'config'
}

// SessionStorage keys for lifecycle objects.
const CONFIG_KEY = 'config'

// Default time-to-live for a third party token we generate is 1 hour.
const DEFAULT_TTL_MILLIS = 1000 * 60 * 60

interface TabEvent {
  type: string,
  payload: JSONValue
}

/**
 * Encapsulates the lifecycle event requests that occur on
 * runner start and termination and when alerts are resolved
 * or rejected.
 *
 * The Lifecycle object also provides static methods to retrieve
 * configuration items, a public and private key pair, and also to
 * produce the signed token that is required in requests from this party
 * to third parties.
 */
export class Lifecycle extends EventTarget {
  static #instance: Lifecycle = new Lifecycle()

  // Expose event names to importers.
  static Ev = Ev

  static getInstance() {
    return this.#instance
  }

  /**
   * All system requests are under the /daemon/ path.
   *
   * @param {Request} request to be tested.
   * @returns {boolean} true if the request is a lifecycle request.
   */
  static shouldHandle(request: Request): boolean {
    return (
      Lifecycle.isConfig(request) ||
      Lifecycle.isEvent(request)
    )
  }

  static isConfig(request: Request): boolean {
    const url = new URL(request.url)
    return (
      request.method == 'POST' &&
      url.pathname == `/${DAEMON_PREFIX}/${CONFIG_PATH}` &&
      request.headers.get('Content-Type') == 'application/json'
    )
  }

  static isEvent(request: Request): boolean {
    const url = new URL(request.url)
    return (
      request.method == 'POST' &&
      url.pathname == `/${DAEMON_PREFIX}/${EVENT_PATH}` &&
      request.headers.get('Content-Type') == 'application/json'
    )
  }

  /**
   * Handles a lifecycle request.
   *
   * @param {Request} request to be handled.
   * @returns {Response} response for caller.
   */
  async handler(request: Request): Promise<Response> {
    if (Lifecycle.isConfig(request)) {
      return await this.handleConfig(request)
    }
    if (Lifecycle.isEvent(request)) {
      return await this.handleEvent(request)
    }
    return response({
      error: 'Invalid daemon request'
    })
  }

  /**
   * Saves the lifecycle config object provided in the request body.
   *
   * Fires a 'config' lifecycle event.
   *
   * @param {Request} request containing the configuration json.
   * @return {Response} ok response.
   */
  async handleConfig(request: Request): Promise<Response> {
    const configJson = await request.json()
    sessionStorage.setItem(CONFIG_KEY, JSON.stringify(configJson))
    this.dispatchEvent(new CustomEvent(Ev.Config, {
      detail: configJson
    }))
    return response({
      ok: true
    })
  }

  /**
   * Fires a lifecycle event upon receiving an event request.
   *
   * @param {Request} request containing the event.
   * @return {Response} ok response.
   */
  async handleEvent(request: Request): Promise<Response> {
    const {
      type,
      payload
    } = await request.json()
    this.dispatchEvent(new CustomEvent(type, {
      detail: payload
    }))
    return response({
      ok: true
    })
  }

  /**
   * Returns the configuration object stored on initialisation.
   * @return {DaemonConfig} the daemon configuration object.
   * @throws {string} exception if config is not yet initialised.
   */
  static getConfig(): DaemonConfig {
    const json = sessionStorage.getItem(CONFIG_KEY)
    if (!json) {
      throw 'DaemonConfig not ready'
    }
    return JSON.parse(json)
  }

  /**
   * Generates if necessary and returns a private key for
   * use in signing tokens.
   *
   * @returns {JSONWebKey} the private key for this session.
   */
  static getPrivateKey(): Promise<JsonWebKey> {
    const {
      system: {
        privateJwk
      }
    } = Lifecycle.getConfig()
    return Promise.resolve(privateJwk)
  }

  /**
   * Returns a token suitable for the `x-tabserver-token` header
   * in a request to the supplied party with the required
   * scope.
   *
   * If party is omitted, it defaults to this party.
   * If
   *
   * @param {Scope} scope map of string source -> space-separated capabilities.
   * @param {string=} party the party host name to whom the request will be sent.
   * @param { src=} src the HTML page from which the request is (actually or notionally) made.
   * @param {number=} ttlMillis the lifetime of this token.
   * @returns {Token} the signed token.
   * @throws {string} if configuration not yet initialised.
   *
  */
  static async getTokenFor(
    scope: Scope,
    party: string | null = null,
    src: string | null = null,
    ttlMillis: number = DEFAULT_TTL_MILLIS,
    pre: Array<{ counterparty: string; src: string }> = []
  ): Promise<Token> {
    const config = Lifecycle.getConfig()
    if (!config) {
      throw 'Lifecycle configuration not yet available'
    }

    // Extract protocol, our party, issuer and source from system config.
    const {
      system: {
        protocol,
        party: us,
        issuer,
        source
      }
    } = config

    const appSourceUrl = new URL(source)

    const aud = party ? `${protocol}//${party}`: `${protocol}//${us}`
    const sub = `${protocol}//${us}`
    const now = Date.now()

    // Emit canonical {counterparty, src} entries so the signed bytes are stable.
    const payload = {
      iss: issuer,
      aud,
      sub,
      src: src ?? `${appSourceUrl.origin}${appSourceUrl.pathname}${appSourceUrl.search}`,
      scope,
      iat: now,
      exp: now + ttlMillis,
      ...(pre.length
        ? { pre: pre.map(({ counterparty, src }) => ({ counterparty, src })) }
        : {})
    }

    const token = new Token(payload)
    await token.signWith(Lifecycle.getPrivateKey())
    return token
  }

  static async getStorage(): Promise<Storage> {
    const token = await Lifecycle.getStorageToken()
    return new Storage(token)
  }

  /**
   * Returns a token for our party with the setitem and getitem
   * capabilities on the party control source.
   */
  static getStorageToken(): Promise<Token> {
    const {
      system: {
        party,
      }
    } = Lifecycle.getConfig()

    const scope = {
      [Token.Source.PARTY_CONTROL]: `${Token.Capability.GET_ITEM} ${Token.Capability.SET_ITEM}`
    }

    return Lifecycle.getTokenFor(scope, party)
  }
}
